import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux"; 
import {
    Paper,
    Avatar,
    Chip,
    Grid,
    Typography,
    IconButton,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as AvatarIcon } from "./chatavatar.svg";
import CloseIcon from "@mui/icons-material/Close";
import { getHelpVideos } from "../../actions/portal_actions";

const StyledHelpPaper = styled(Paper)(({ theme }) => ({
    zIndex: 99999,
    position: 'fixed',
    bottom: "0px",
    left: "0px",
    backgroundColor: "#fff",
    padding: "10px",
}));

const StyledHelpChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== 'ischatpage',
})(({ theme, ischatpage }) => ({
    zIndex: 99999,
    position: 'fixed',
    left: "10px",
    margin: "10px",
    backgroundColor: "#fff",
    padding: "10px",
    cursor: 'grab',
    ...(ischatpage
        ? {
            [theme.breakpoints.down("sm")]: {
                top: "65px",
            },
            [theme.breakpoints.up("sm")]: {
                bottom: "0px",
            },
        }
        : {
            bottom: "0px",
        }),
}));


export default function Help({ isAuthenticated }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(0);
    const [currentPath, setCurrentPath] = useState(undefined);
    const [helpObject, setHelpObject] = useState(false);
    const [position, setPosition] = useState({ x: 10, y: window.innerHeight - 80 });

    const [isDragging, setIsDragging] = useState(false);
    const dragStartPos = useRef({ x: 0, y: 0 });
    const hasDragged = useRef(false);
    const [paperPosition, setPaperPosition] = useState({ bottom: '10px', left: '10px' });

    useEffect(() => {
        async function pathChange() {
            if (location.pathname !== currentPath || !helpObject) {
                setCurrentPath(location.pathname);
                const videos = await dispatch(getHelpVideos(location.pathname));
                setHelpObject(videos);
            }
        }
        pathChange();
    }, [location, isAuthenticated, currentPath, helpObject]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            hasDragged.current = true;
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            const newX = clientX - dragStartPos.current.x;
            const newY = clientY - dragStartPos.current.y;
            setPosition({ x: newX, y: newY });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            document.body.style.cursor = 'default';
        };

        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("touchmove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
            window.addEventListener("touchend", handleMouseUp);
            return () => {
                window.removeEventListener("mousemove", handleMouseMove);
                window.removeEventListener("touchmove", handleMouseMove);
                window.removeEventListener("mouseup", handleMouseUp);
                window.removeEventListener("touchend", handleMouseUp);
            };
        }
    }, [isDragging, position.x, position.y]);
 
    const isChatPage = location.pathname?.includes("chat");

    const handleDragStart = (e) => {
        setIsDragging(true);
        hasDragged.current = false; // Reset drag status on new mousedown
        document.body.style.cursor = 'grabbing';
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        dragStartPos.current = { x: clientX - position.x, y: clientY - position.y };
    };

    const handleClick = () => {
        if (hasDragged.current) return;

        const screenCenterX = window.innerWidth / 2;
        const screenCenterY = window.innerHeight / 2;
        const margin = window.innerWidth < 600 ? '0px' : '10px';
        let newPaperPos = {};

        if (position.y < screenCenterY) { // Top half
            newPaperPos.top = margin;
            newPaperPos.bottom = 'auto';
        } else { // Bottom half
            newPaperPos.bottom = margin;
            newPaperPos.top = 'auto';
        }

        if (position.x < screenCenterX) { // Left half
            newPaperPos.left = margin;
            newPaperPos.right = 'auto';
        } else { // Right half
            newPaperPos.right = margin;
            newPaperPos.left = 'auto';
        }

        setPaperPosition(newPaperPos);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    if (helpObject && helpObject.videos) {
        if (open) {
            return (
                <StyledHelpPaper elevation={3} style={paperPosition}>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Grid item size>
                            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar sx={{ backgroundColor: "#fff" }}>
                                    <AvatarIcon />
                                </Avatar>
                                Need Help?
                            </Typography>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 1 }} />
                    <Typography sx={{ paddingBlock: "10px" }}>
                        Some of these videos might help you get up to speed with this page
                    </Typography>
                    {helpObject.videos.map((item, index) => {
                        const videoURL = "https://embed.app.guidde.com/playbooks/" + item.id;
                        return (
                            <Accordion key={item.id} expanded={expanded === index} onChange={() => setExpanded(index)}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`panel${index}-content`}
                                    id={`panel${index}-header`}
                                >
                                    {item.title}
                                </AccordionSummary>
                                <AccordionDetails>
                                    <iframe
                                        width="100%"
                                        height="250px"
                                        src={videoURL}
                                        title={item.title}
                                        frameBorder="0"
                                        referrerPolicy="unsafe-url"
                                        allowFullScreen="true"
                                        allow="clipboard-write"
                                        sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                                    />
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </StyledHelpPaper>
            );
        } else {
            return (
                <StyledHelpChip
                    style={{
                        left: `${position.x}px`,
                        top: `${position.y}px`,
                        bottom: 'auto',
                        right: 'auto',
                    }}
                    ischatpage={isChatPage}
                    onClick={handleClick}
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                    avatar={<Avatar><AvatarIcon /></Avatar>}
                    label="Need Help?"
                    variant="outlined"
                />
            );
        }
    } else {
        return <></>; 
    }
}